import React from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import { Brandfolder, Collection } from "@helpers/show_page_helpers";
import Tooltip from "@components/common/tooltip/main";
import getSuggestions from '@api/v3/brandfolders/autosuggest_search';

import SectionsContext from '../sectionsContext';
import "./styles/search-container";
import Autocomplete from "./autocomplete";
import SearchFilterPinsMenu from './search_filter_pins_menu';

class SectionSearch extends React.Component {
  constructor(props) {
    super(props);

    const { searchQuery } = this.props;
    this.state = {
      searchQuery,
      suggestions: [],
      showAutocomplete: false,
    };
  }

  onQueryChange = (searchQuery) => {
    const { brandfolderKey, submitSearchQuery } = this.props;
    const oldSearchQuery = this.state.searchQuery;

    const collection = Boolean(Collection.key);

    getSuggestions({
      resourceKey: collection ? Collection.key : brandfolderKey,
      query: searchQuery,
      collection,
    }).then((suggestions) => {
      this.setState({ suggestions });
    });

    this.setState({
      searchQuery,
      showAutocomplete: true,
    }, () => {
      if (oldSearchQuery !== '' && searchQuery === '') {
        submitSearchQuery(searchQuery, true);
      }
    });
  }

  shareSearch = () => {
    BF.Share.shareResource.call();
    if (Collection.key) {
      BF.fx.dataLayerPush({
        collection: BF.fx.collectionSlug(),
        brandfolder: BF.fx.slug(),
        event: "CollectionShare"
      });
    } else {
      BF.fx.dataLayerPush({
        brandfolder: BF.fx.slug(),
        event: "BrandfolderShare"
      });
    }
  };

  onSubmit = (e) => {
    e.preventDefault();
    const { submitSearchQuery } = this.props;
    const { searchQuery } = this.state;
    submitSearchQuery(searchQuery, true);
  }

  renderSearchActions() {
    const { addRemovePinnedSearch, editable, searchFilters, submitSearchQuery } = this.props;
    const { searchQuery } = this.state;
    const pinned = searchFilters.map((filter) => filter.query).indexOf(searchQuery) > -1;
    return (
      <span className="search-actions">
        {editable ? (
          <Tooltip
            tooltipId="brandfolder-pin"
            tooltipContent={pinned ? t("Unpin Search") : t("Pin Search")}
          >
            <div className="search-icon-container">
              <button
                type="button"
                className={`bff-pin ${pinned ? "unpin" : ""}`}
                aria-label={t("Pin Search")}
                onClick={addRemovePinnedSearch}
              />
            </div>
          </Tooltip>
        ) : ''}
        {editable ? (
          <Tooltip
            tooltipId="brandfolder-share-search"
            tooltipContent={t("Share Search")}
          >
            <div className="search-icon-container">
              <button
                type="button"
                className="bff-share share-search"
                aria-label={t("Share Search")}
                onClick={this.shareSearch}
              />
            </div>
          </Tooltip>
        ) : ''}
        <Tooltip
          tooltipId="brandfolder-clear-search"
          tooltipContent={t("Clear Search")}
        >
          <div className="search-icon-container">
            <button
              type="button"
              className="bff-close"
              aria-label={t("Clear Search")}
              onClick={() => submitSearchQuery(null)}
            />
          </div>
        </Tooltip>
      </span>
    );
  }

  render() {
    const {
      editable,
      searchFilters,
      searchSubmitted,
      toggleFiltersDrawer,
      toggleListView,
      updateSearchQuery,
      updateSortOrder
    } = this.props;

    const {
      searchQuery,
      suggestions,
      showAutocomplete: autocompleteOpen,
    } = this.state;

    const searchDisabled = !searchQuery || searchQuery.length < 1;
    const showAutocomplete = autocompleteOpen && !searchDisabled && suggestions.length && searchQuery.length;

    // Our friends at Cambro aren't fans of the autocomplete, so we'll
    // just disable it for them to make everyone's lives easier.
    const isCambro = BF.fx.organization().key === 'p824zf-2eqqzc-eaeg8';

    return (
      <SectionsContext.Consumer>
        {({ listView, sortBy, submitSearchQuery }) => (
          <div className="search-container">
            <div className="flex-row flex-column">
              <div className="search-and-filter">
                <div className="filter-button-container">
                  <div
                    className="advanced-filter-toggle"
                    onClick={() => toggleFiltersDrawer('section-search')}
                  >
                    <button
                      type="button"
                      className="button secondary sm"
                    >
                      <span className="bff-filters search-filter-toggle" />
                      <span className="search-filter-toggle-label">{t('Filters')}</span>
                    </button>
                  </div>
                  {(editable || searchFilters.length > 0) && (
                    <SearchFilterPinsMenu
                      editable={editable}
                      searchFilters={searchFilters}
                      submitSearchQuery={submitSearchQuery}
                    />
                  )}
                </div>
                <div className="search-input-container">
                  <label>
                    <span className="bff-search" aria-label="hidden" />
                  </label>
                  <form
                    autoComplete="off"
                    onSubmit={this.onSubmit}
                  >
                    <input
                      type="text"
                      placeholder={t("Search")}
                      id="brandfolderSearch"
                      value={searchQuery || ""}
                      data-resource-key={
                        Collection.key ? Collection.key : Brandfolder.key
                      }
                      data-resource-type={
                        Collection.key ? t("Collection") : "Brandfolder"
                      }
                      onChange={({ target }) => this.onQueryChange(target.value)}
                      onBlur={() => setTimeout(() => this.setState({ showAutocomplete: false }), 500)}
                    />
                    {searchSubmitted && searchQuery && searchQuery.length > 0 ? this.renderSearchActions() : ""}
                    <button
                      type="submit"
                      className={`button primary sm search-button ${searchDisabled ? "disabled" : ""}`}
                      disabled={searchDisabled}
                      onClick={() => submitSearchQuery(searchQuery, true)}
                    >
                      {t('Search')}
                    </button>
                    <button
                      type="button"
                      className={`button primary sm search-button-mobile ${searchDisabled ? "disabled" : ""}`}
                      disabled={searchDisabled}
                    >
                      <span className="bff-search" aria-label="hidden" />
                    </button>

                    {showAutocomplete && !isCambro ? (
                      <Autocomplete
                        suggestions={suggestions}
                        onSelect={(search) => updateSearchQuery(null, search)}
                        onClose={() => this.setState({ showAutocomplete: false })}
                      />
                    ) : null}
                  </form>
                </div>
              </div>
              <div className="sort-and-display">
                <div className="sort-options">
                  <Tooltip class="type-light" tooltipId="sort-options" tooltipContent={t('Sort by')}>
                    <label className="sort-label">
                      {t('Sort by')}:
                    </label>
                  </Tooltip>
                  <Select
                    name="asset-sort"
                    value={sortBy}
                    options={[
                      { value: "position ASC", label: t("Position") },
                      { value: "name ASC", label: t("Name") },
                      { value: "score DESC", label: t("Popularity") },
                      { value: "updated_at DESC", label: t("Updated") },
                      { value: "created_at DESC", label: t("Created") }
                    ]}
                    clearable={false}
                    searchable={false}
                    onChange={(option) => updateSortOrder(option.value)}
                  />
                </div>
                <div className="sort-display">
                  <button
                    type="button"
                    className={`bff-grid-view ${!listView ? "active" : "disabled"}`}
                    onClick={() => { if (listView) toggleListView(); }}
                  />
                  <button
                    type="button"
                    className={`bff-list-view ${listView ? "active" : "disabled"}`}
                    onClick={() => { if (!listView) toggleListView(); }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </SectionsContext.Consumer>
    );
  }
}

SectionSearch.propTypes = {
  addRemovePinnedSearch: PropTypes.func.isRequired,
  brandfolderKey: PropTypes.string.isRequired,
  editable: PropTypes.bool.isRequired,
  searchFilters: PropTypes.arrayOf(PropTypes.shape({})),
  searchSubmitted: PropTypes.bool.isRequired,
  searchQuery: PropTypes.string,
  submitSearchQuery: PropTypes.func.isRequired,
  toggleFiltersDrawer: PropTypes.func.isRequired,
  toggleListView: PropTypes.func.isRequired,
  updateSearchQuery: PropTypes.func.isRequired,
  updateSortOrder: PropTypes.func.isRequired,
};

SectionSearch.defaultProps = {
  searchFilters: [],
  searchQuery: '',
};

export default SectionSearch;
